import { motion } from "framer-motion";
import { Info, Clock, Shield, ArrowRight, Sparkles } from "lucide-react";
import { useI18n } from "../i18n";

const PriceRow = ({ item, i }) => (
  <motion.li
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.4, delay: i * 0.03 }}
    className="grid grid-cols-12 gap-3 md:gap-6 items-start py-4 border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors duration-300 px-1"
  >
    <span className="col-span-12 md:col-span-4 text-neutral-100 text-sm md:text-base font-medium">
      {item.label}
    </span>
    <span className="col-span-8 md:col-span-6 text-neutral-500 text-xs md:text-sm font-light leading-relaxed">
      {item.note}
    </span>
    <span className="col-span-4 md:col-span-2 text-right font-mono-accent text-sm md:text-base text-cyan whitespace-nowrap tabular-nums">
      {item.price}
    </span>
  </motion.li>
);

const PhaseGroup = ({ group, phaseIdx, groupIdx, priceFromLabel, colLabel, colDesc, colPrice }) => (
  <motion.div
    data-testid={`phase-${phaseIdx}-group-${groupIdx}`}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6 }}
    className="rounded-2xl border border-white/10 bg-[#0F1115] p-6 md:p-8"
  >
    <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
      <div className="min-w-0 flex-1">
        <h4 className="font-display font-700 uppercase tracking-tight text-lg md:text-xl leading-tight text-white">
          {group.title}
        </h4>
        <p className="text-neutral-400 text-sm font-light mt-1">{group.desc}</p>
      </div>
      <span className="shrink-0 font-mono-accent text-[9px] uppercase tracking-[0.22em] text-cyan/70 border border-cyan-500/30 rounded-full px-3 py-1">
        {priceFromLabel}
      </span>
    </div>

    <div className="mt-6 hidden md:grid grid-cols-12 gap-6 pb-3 border-b border-cyan-500/20 px-1 font-mono-accent text-[10px] uppercase tracking-[0.22em] text-cyan/80">
      <span className="col-span-4">{colLabel}</span>
      <span className="col-span-6">{colDesc}</span>
      <span className="col-span-2 text-right">{colPrice}</span>
    </div>

    <ul className="mt-1">
      {group.items.map((it, i) => (
        <PriceRow key={i} item={it} i={i} />
      ))}
    </ul>
  </motion.div>
);

export const Pricing = ({ onQuote }) => {
  const { t } = useI18n();
  const phase1 = t("pricing.phase1");
  const phase2 = t("pricing.phase2");
  const priceFromLabel = t("pricing.priceFrom");
  const colLabel = t("pricing.colHeaderLabel");
  const colDesc = t("pricing.colHeaderDesc");
  const colPrice = t("pricing.colHeaderPrice");

  return (
    <section
      id="tarifs"
      data-testid="pricing-section"
      className="relative py-24 md:py-32 px-6 md:px-10 bg-[#070809] border-y border-cyan-500/10 overflow-hidden"
    >
      <div className="absolute inset-0 radial-cyan pointer-events-none opacity-40" />

      <div className="relative max-w-[1400px] mx-auto">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <span className="font-mono-accent text-xs uppercase tracking-[0.3em] text-cyan">
              {t("pricing.tag")}
            </span>
            <div className="flex items-center gap-3 mt-4 flex-wrap">
              <h2 className="font-display font-700 uppercase tracking-tighter text-4xl md:text-6xl leading-none">
                {t("pricing.title1")}{" "}
                <span className="text-cyan neon-text-glow">{t("pricing.title2")}</span>
              </h2>
            </div>
          </div>
          <p className="max-w-sm text-neutral-400 font-light">{t("pricing.subtitle")}</p>
        </div>

        {/* Materials strip */}
        <div
          data-testid="pricing-materials-strip"
          className="mb-10 flex flex-wrap items-center gap-4 rounded-2xl border border-cyan-500/25 bg-[#0F1115] px-5 py-4"
        >
          <div className="inline-flex items-center gap-2 shrink-0">
            <Shield size={18} className="text-cyan" />
            <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-cyan/80">
              {t("pricing.materialsTitle")}
            </span>
          </div>
          <span className="text-neutral-300 text-sm md:text-base font-medium">{t("pricing.materials")}</span>
        </div>

        {/* PHASE 1 */}
        <div className="mb-16" data-testid="phase-1-block">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-6 rounded-2xl border-l-2 border-cyan-400 bg-white/[0.02] pl-6 md:pl-8 pr-6 py-5">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-cyan-400/15 border border-cyan-400/40 px-3 py-1 font-mono-accent text-[10px] uppercase tracking-[0.25em] text-cyan mb-3">
                <Sparkles size={12} /> {phase1.label}
              </div>
              <p className="text-neutral-300 font-light text-sm md:text-base leading-relaxed">
                {phase1.intro}
              </p>
            </div>
            <button
              data-testid="phase-1-quote-cta"
              onClick={onQuote}
              className="shrink-0 self-start inline-flex items-center gap-2 text-black font-medium text-sm px-5 py-3 rounded-full neon-glow hover:scale-[1.03] active:scale-95 transition-transform duration-300"
              style={{ backgroundColor: "#C9A96A" }}
            >
              {t("pricing.quoteCta")} <ArrowRight size={14} />
            </button>
          </div>

          <div className="grid grid-cols-1 gap-5">
            {phase1.groups.map((g, i) => (
              <PhaseGroup
                key={i}
                group={g}
                phaseIdx={1}
                groupIdx={i}
                priceFromLabel={priceFromLabel}
                colLabel={colLabel}
                colDesc={colDesc}
                colPrice={colPrice}
              />
            ))}
          </div>
        </div>

        {/* PHASE 2 */}
        <div data-testid="phase-2-block" className="relative">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-6 rounded-2xl border-l-2 border-white/20 bg-white/[0.015] pl-6 md:pl-8 pr-6 py-5">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/[0.04] border border-white/15 px-3 py-1 font-mono-accent text-[10px] uppercase tracking-[0.25em] text-neutral-300">
                  <Sparkles size={12} /> {phase2.label}
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-cyan-400/10 border border-cyan-400/30 px-2.5 py-1 font-mono-accent text-[9px] uppercase tracking-[0.22em] text-cyan">
                  <Clock size={10} /> {phase2.badge}
                </span>
              </div>
              <p className="text-neutral-400 font-light text-sm md:text-base leading-relaxed">
                {phase2.intro}
              </p>
            </div>
            <button
              data-testid="phase-2-quote-cta"
              onClick={onQuote}
              className="shrink-0 self-start inline-flex items-center gap-2 border border-cyan-400/50 text-cyan font-medium text-sm px-5 py-3 rounded-full hover:bg-cyan-400 hover:text-black transition-colors duration-300"
            >
              {t("pricing.quoteCta")} <ArrowRight size={14} />
            </button>
          </div>

          <div className="grid grid-cols-1 gap-5 opacity-90">
            {phase2.groups.map((g, i) => (
              <PhaseGroup
                key={i}
                group={g}
                phaseIdx={2}
                groupIdx={i}
                priceFromLabel={priceFromLabel}
                colLabel={colLabel}
                colDesc={colDesc}
                colPrice={colPrice}
              />
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div
          data-testid="pricing-disclaimer"
          className="mt-12 flex items-start gap-3 rounded-xl border-l-2 border-cyan-400 bg-white/[0.02] px-5 py-4"
        >
          <Info size={18} className="text-cyan shrink-0 mt-0.5" />
          <p className="text-neutral-400 text-sm font-light leading-relaxed">
            {t("pricing.disclaimer")}
          </p>
        </div>
      </div>
    </section>
  );
};
